/**
 * UX Sprint 1 — Single user-facing workflow timeline.
 *
 * Renders the 8 USER_STAGES as one premium horizontal rail. Replaces:
 *   - the old hand-rolled stage timeline (ProjectBoard.jsx L519–534)
 *   - the duplicate phase chips inside AutoPipelineBar
 *
 * Pure UX layer:
 *   - Status derived from existing `project.stages[*].status`.
 *   - Selecting a stage just calls `onSelect(userStageKey)` which the
 *     parent maps to the existing `tab` key — every existing tab panel
 *     and route stays intact.
 */
import { Check, Loader2, AlertTriangle, Clock, CircleDot } from "lucide-react";
import {
    USER_STAGES,
    userStageStatus,
    userStageStatusColor,
    userStageStatusLabel,
} from "@/lib/constants";

const STATUS_ICON = {
    approved: Check,
    in_progress: Loader2,
    needs_review: CircleDot,
    failed: AlertTriangle,
    not_started: Clock,
};

export default function WorkflowTimeline({ project, activeKey, onSelect }) {
    return (
        <nav
            data-testid="workflow-timeline"
            aria-label="Project workflow"
            className="mt-6 -mx-1 overflow-x-auto scrollbar-thin"
        >
            <ol className="flex gap-2 px-1 min-w-max">
                {USER_STAGES.map((us, idx) => {
                    const status = userStageStatus(us, project?.stages);
                    const Icon = STATUS_ICON[status] || Clock;
                    const isActive = activeKey === us.key;
                    const statusClass = userStageStatusColor(status);

                    return (
                        <li key={us.key}>
                            <button
                                type="button"
                                onClick={() => onSelect && onSelect(us)}
                                data-testid={`timeline-stage-${us.key}`}
                                data-status={status}
                                aria-current={isActive ? "step" : undefined}
                                className={`group flex items-center gap-3 px-4 py-3 border rounded-sm transition-all text-left
                                    ${isActive
                                        ? "border-white bg-zinc-900 shadow-[0_0_0_1px_rgba(255,68,0,0.35)]"
                                        : `${statusClass} hover:border-zinc-700 hover:bg-zinc-900/40`}`}
                            >
                                <span className={`text-[10px] font-mono uppercase tracking-wider ${
                                    isActive ? "text-[#FF4400]" : "text-zinc-600"
                                }`}>
                                    {String(idx + 1).padStart(2, "0")}
                                </span>
                                <span className="flex flex-col leading-tight">
                                    <span className={`text-sm ${isActive ? "text-white" : ""}`}>
                                        {us.label}
                                    </span>
                                    <span className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider mt-0.5">
                                        <Icon className={`w-3 h-3 ${status === "in_progress" ? "animate-spin" : ""}`} />
                                        {userStageStatusLabel(status, us, project?.stages)}
                                    </span>
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
}
